'use client';

import { useModal } from '@/components/ModalContext';
import { useProduct } from '@/contexts/ProductContext';
import { getSelectedOptionValue } from '@/utils/product/product-context-helpers';
import {
  getShopifyOptionByName,
  BIRCH_SIZE_OPTION_NAME,
} from '@/components/blocks/variantOptions/shopifyOptionConventions';
import { rteHasContent } from '@/utils/rte';
import RteModal from './RteModal';

// BL-650: dimensions come from Shopify's `custom.dimensions` variant
// metafield — a `list.dimension` JSON array in [width, length, height]
// order, e.g. [{"value":38,"unit":"INCHES"},...]. Unlike the
// productWidth/Length/Height metafields the size-chart modal reads (null on
// every real variant), this one is actually populated.
//
// BL-777: the metafield array is positional and unlabelled — index 0 is width,
// 1 is length, 2 is height — so only the trailing height may be omitted. Some
// variants publish just [width, length]; those previously rendered nothing.
function formatDimensions(rawValue) {
  if (!rawValue) return null;
  let dims;
  try {
    dims = JSON.parse(rawValue);
  } catch {
    return null;
  }
  if (!Array.isArray(dims)) return null;

  const unitLabel = (unit) => (unit === 'INCHES' ? '"' : ` ${unit?.toLowerCase() ?? ''}`);
  const format = (dim, label) =>
    dim?.value == null ? null : `${dim.value}${unitLabel(dim.unit)} ${label}`;

  const width = format(dims[0], 'W');
  const length = format(dims[1], 'L');
  if (!width || !length) return null;

  const height = format(dims[2], 'H');
  return [width, length, height].filter(Boolean).join(' x ');
}

// BL-774: columns are derived from the tile count so rows split evenly (e.g.
// 6 tiles -> 3+3, 4 tiles -> 2+2) instead of the old fixed 4-col grid, which
// left a lopsided 4+2 split. An uneven last row only happens when the count
// itself is odd.
function getGridColumns(count) {
  const rows = Math.ceil(count / 3);
  return Math.ceil(count / rows);
}

// BL-650: V2 buy box size selector — title-only cards in a 4-col grid, no
// description or price diff (unlike VariantButtons/Cooling). Layout and the
// selected-state double-border technique follow Helix's V2 buy box
// (helixsleep.com), translated to this repo's border-sea accent instead of
// their brand blue.
//
// `section_label` is a display heading only (e.g. "Mattress Size") — it is
// NOT the Shopify option name to match against, same distinction as BL-651's
// section_label vs BIRCH_COOLING_OPTION_NAME. That match always uses
// BIRCH_SIZE_OPTION_NAME.
const SizeButtons = ({ block }) => {
  const { selectedVariant, updateOptions, productData } = useProduct();
  const { openModal } = useModal();

  const { section_label, more_information } = block ?? {};
  const heading = section_label || BIRCH_SIZE_OPTION_NAME;
  // BL-926: the size-guide link is authored content only — no hardcoded label and
  // no Shopify-derived fallback modal. Both halves have to be authored or the
  // button is not rendered at all.
  const linkLabel = more_information?.link_label;
  const modalContent = more_information?.modal_content;
  const showSizeGuide = Boolean(linkLabel?.trim()) && rteHasContent(modalContent);

  const shopifyProduct = productData?.shopifyProduct;
  const shopifyOption = getShopifyOptionByName(shopifyProduct, BIRCH_SIZE_OPTION_NAME);
  const currentValue = getSelectedOptionValue(selectedVariant, BIRCH_SIZE_OPTION_NAME);
  const dimensionsText = formatDimensions(selectedVariant?.customDimensions?.value);

  if (!shopifyOption || !selectedVariant) return null;

  const openSizeGuide = () => openModal(<RteModal content={modalContent} />, 'max-w-2xl', 'bg-white');

  return (
    <section className="flex flex-col gap-4 md:my-4 font-worksans text-primary" data-block="size_buttons">
      <div className="flex flex-col items-start gap-1 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
        <p className="text-xl font-medium leading-5">{heading}</p>
        {showSizeGuide && (
          <button
            type="button"
            className="shrink-0 cursor-pointer text-left sm:text-right text-sm leading-5 text-sea underline"
            onClick={openSizeGuide}
          >
            {linkLabel}
          </button>
        )}
      </div>

      <fieldset
        className="grid gap-2 border-0 p-0"
        style={{ gridTemplateColumns: `repeat(${getGridColumns(shopifyOption.values.length)}, minmax(0, 1fr))` }}
      >
        <legend className="sr-only">{heading}</legend>
        {shopifyOption.values.map((optionValue) => {
          const isSelected = currentValue === optionValue;

          return (
            <label
              key={optionValue}
              // BL-775: square corners. The comp gives the variant selectors a
              // 0px radius; these carried `rounded-xl` from the Helix markup
              // they were modelled on. The cooling options next to them were
              // already square, so this is what made the two read as different
              // controls.
              // BL-789: fixed tile height per Figma (56px desktop, 53px mobile) —
              // priority over the text's breathing room, so horizontal padding
              // is cut down to px-1. Two-value labels (e.g. "King / CA King")
              // still need to wrap to two lines within that height, so the
              // label itself isn't forced to nowrap — px-1 alone gives
              // single-word sizes ("Twin XL") enough room to stay on one line.
              className={`relative flex h-[53px] cursor-pointer items-center justify-center border bg-white px-1 outline-none transition-colors duration-150 hover:ring-1 hover:ring-inset hover:ring-sand has-focus-visible:ring-2 has-focus-visible:ring-sea has-focus-visible:ring-offset-2 md:h-14 ${
                isSelected ? 'border-sea' : 'border-sand'
              }`}
            >
              <input
                type="radio"
                name={`variant-${BIRCH_SIZE_OPTION_NAME}`}
                value={optionValue}
                checked={isSelected}
                onChange={() =>
                  updateOptions([{ name: BIRCH_SIZE_OPTION_NAME, value: optionValue }], { persist: true })
                }
                className="sr-only"
              />
              <span className="text-center text-sm sm:text-base font-medium">{optionValue}</span>
              {/* Selected-state ring as an overlay, not a border-width change, so the
                  card doesn't shift size/layout on select — same technique VariantButtons uses.
                  Overlay width matches Helix's own markup (2.5px), and the label border
                  switches colour with it so the two edges never read as separate rings.
                  BL-775: it used to carry `rounded-lg` against the label's `rounded-xl`;
                  with the label square the ring has to be square too, or the selected
                  size shows rounded corners inside a square tile.
                  BL-650 review: the focus ring above is has-focus-visible, not focus-within —
                  focus-within also fires on mouse click, stacking an offset ring outside this
                  border and reading as a double blue border on the selected size. Helix has no
                  focus ring at all here (focus:outline-none); keeping ours is a deliberate a11y
                  improvement and is keyboard-only, so it can't reproduce that double border. */}
              <span
                aria-hidden="true"
                className={`pointer-events-none absolute -inset-px border-[2.5px] ${
                  isSelected ? 'border-sea' : 'border-transparent'
                }`}
              />
            </label>
          );
        })}
      </fieldset>

      {dimensionsText && (
        <p className="text-sm text-primary">{dimensionsText}</p>
      )}
    </section>
  );
};

export default SizeButtons;
